// RUSH AI — زر عائم + Chat Panel.
// - يستدعي POST /api/rush-ai/chat مع session_id ثابت لكل زيارة.
// - Quick actions: احسب عجلة / سعر CP / تركيبة سلاح / مشكلة في اللعبة / أشحن.
// - تنسيق كحلي/ذهبي متطابق مع هوية Rush Store.
import React, { useEffect, useRef, useState } from "react";
import { publicApi, formatApiError } from "../lib/api";
import { Bot, X, Send, Trash2, Loader2, Sparkles } from "lucide-react";
import "./RushAIChat.css";

const SESSION_KEY = "rush_ai_session";
const HISTORY_KEY = "rush_ai_history";

const QUICK_ACTIONS = [
  { icon: "🎯", label: "احسب لي عجلة", text: "أبي أحسب تكلفة عجلة، ساعدني خطوة خطوة." },
  { icon: "💰", label: "كم سعر CP؟", text: "وش أسعار باقات CP الحالية؟" },
  { icon: "🎮", label: "أبي تركيبة سلاح", text: "أبي أفضل تركيبة سلاح مثلاً BP50 للرانك." },
  { icon: "🛠", label: "عندي مشكلة في اللعبة", text: "عندي مشكلة تسجيل دخول في Activision." },
  { icon: "🛒", label: "أبي أشحن", text: "أحتاج أشحن CP، وش أنسب باقة؟" },
];

const initialAssistant = {
  role: "assistant",
  content:
    "أهلاً بك في RUSH AI 👋\nأنا مساعدك لحساب عجلات كود موبايل وشراء CP من متجر Rush Store. اختر أحد الأزرار السريعة أو اسألني مباشرة.",
};

const loadHistory = () => {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [initialAssistant];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return [initialAssistant];
    return arr;
  } catch {
    return [initialAssistant];
  }
};

const saveHistory = (msgs) => {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-40)));
  } catch {}
};

const RushAIChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  });
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    saveHistory(messages);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const message = (text || "").trim();
    if (!message || sending) return;
    setError("");
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setSending(true);
    try {
      const payload = { message };
      if (sessionId) payload.session_id = sessionId;
      const { data } = await publicApi.post("/rush-ai/chat", payload);
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try {
          sessionStorage.setItem(SESSION_KEY, data.session_id);
        } catch {}
      }
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply || "…" }]);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `تعذّر الرد الآن: ${msg}` },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    try {
      if (sessionId) {
        await publicApi.post("/rush-ai/reset", { session_id: sessionId });
      }
    } catch {}
    try {
      sessionStorage.removeItem(HISTORY_KEY);
      sessionStorage.removeItem(SESSION_KEY);
    } catch {}
    setSessionId("");
    setMessages([initialAssistant]);
    setError("");
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        data-testid="rush-ai-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="RUSH AI"
        className={`rush-ai-fab ${open ? "rush-ai-fab--open" : ""}`}
      >
        {open ? <X className="w-5 h-5" /> : (
          <>
            <Bot className="w-5 h-5" />
            <span className="rush-ai-fab__label">RUSH AI</span>
          </>
        )}
      </button>

      {open && (
        <div className="rush-ai-panel" role="dialog" aria-label="RUSH AI Chat" dir="rtl">
          {/* Header */}
          <div className="rush-ai-panel__header">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-b from-[#E5CB94] to-[#A67A38] text-[#0A1128] flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[#F5EAC9] font-black text-sm">RUSH AI</div>
                <div className="text-white/40 text-[10px]">مساعد Rush Store</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="rush-ai-clear"
                onClick={handleClear}
                title="مسح المحادثة"
                className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 flex items-center justify-center transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                data-testid="rush-ai-close"
                onClick={() => setOpen(false)}
                title="إغلاق"
                className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 flex items-center justify-center transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="rush-ai-panel__messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`rush-ai-msg ${m.role === "user" ? "rush-ai-msg--user" : "rush-ai-msg--bot"}`}
              >
                <div className="rush-ai-msg__bubble">{m.content}</div>
              </div>
            ))}
            {sending && (
              <div className="rush-ai-msg rush-ai-msg--bot">
                <div className="rush-ai-msg__bubble rush-ai-msg__bubble--typing">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>يفكر…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick actions */}
          {messages.length <= 2 && !sending && (
            <div className="rush-ai-panel__quick">
              {QUICK_ACTIONS.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  data-testid={`rush-ai-quick-${q.label}`}
                  onClick={() => sendMessage(q.text)}
                  className="rush-ai-quick-btn"
                >
                  <span>{q.icon}</span>
                  <span>{q.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            className="rush-ai-panel__input"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <input
              type="text"
              data-testid="rush-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="اسأل RUSH AI…"
              dir="auto"
              maxLength={1000}
            />
            <button
              type="submit"
              data-testid="rush-ai-send"
              disabled={sending || !input.trim()}
              aria-label="إرسال"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default RushAIChat;
